<template>
  <loader-content :ref="(el: any) => {loader = el}">
  <Header :class="[route.meta.containerClass]"></Header>
  <div id="content" class="container" :class="[route.meta.containerClass]">
    <router-view/>
    <Menu></Menu>
  </div>

  <Popups :ref="(el: any) => popups = el"></Popups>
  <notifications :class="[route.meta.containerClass]" position="bottom center"/>
  </loader-content>
</template>
<script lang="ts">
import {defineComponent, onBeforeUnmount, onMounted, onUnmounted, ref, watch} from 'vue'

import {useStore} from "vuex";
import {reactive, toRefs} from "@vue/reactivity";
import router from "@/router";
import {$t} from "@/i18n";
import SvgIcon from "@/components/SvgIcon.vue";
import {numberFormatter} from "@/service/NumberFormatter";
import Header from "@/components/Header.vue";
import Menu from "@/components/Menu.vue";
import {setupAxios, setupAxiosDefaults} from "@/service/helper";
import {apiAxios} from "@/service/APIClient";
import {useRoute} from "vue-router";
import UserDTO from "@/models/UserDTO";
import { AppEvents } from "./models/enum/AppEvents";
import {useNotification} from "@kyvg/vue3-notification";
import {nowState} from "@/service/DateHelper";
import Popups from "@/components/Popups.vue";
import {openXsollaCheckout} from "@/service/XsollaCheckout";
import LoaderContent from "@/components/LoaderContent.vue";

export default defineComponent({
  name: 'App',
  components: {
    LoaderContent,
    Popups,
    Menu,
    Header,
    SvgIcon
  },
  methods: {$t},
  setup() {
    const loader = ref<InstanceType<typeof LoaderContent> | null>(null)
    const paymentProvider = (process.env.VUE_APP_PAYMENT_PROVIDER ?? 'XSOLLA') as string
    const route = useRoute()
    const store = useStore()
    const state = reactive({
      inited: false,
      userUpdateInterval: null as number|any,
      loginUpdateInterval: null as number|any,
    });

    const notification = useNotification()
    const popups = ref<InstanceType<typeof Popups> | null>(null)

    const events = store.subscribeAction({
      before: (action) => {
        if (action.type !== 'emit') {
          return;
        }
        switch (action.payload?.event) {
          case AppEvents.SHOW_SELECT_GAME_TO_PLAY:
            popups.value?.getPopups().selectGameToPlayPopup.show()
            break;
          case AppEvents.BUY_ITEM:
            switch (paymentProvider) {
              case 'SELF':
                popups.value?.getPopups().internalStorePopup.show(action.payload?.data)
                break;
              case 'XSOLLA':
                loader.value?.load(async () => {
                  await openXsollaCheckout(action.payload?.data, store)
                })
                break;
            }
            break;
          case AppEvents.SHOW_LOOT_BOX:
            popups.value?.getPopups().lootboxPopup.show(action.payload?.data)
            break;
          case AppEvents.PURCHASE_COMPLETED:
            store.dispatch('updateUser')
            notification.notify({type: 'success', title: $t('notifications.purchase_completed'), duration: 1500})
            break;
          case AppEvents.PURCHASE_CANCELED:
            notification.notify({title: $t('notifications.purchase_canceled'), duration: 1500})

            break;
          case AppEvents.PURCHASE_FAILED:
            break;
        }
      },
    })

    onBeforeUnmount(events)

    onMounted(async () => {
      nowState.nowUpdateInterval = setInterval(() => {
        nowState.now = Date.now();
      }, 1000);
      setupAxios(store, apiAxios, router);

      if (store.getters.isLoggedIn) {
        await setupUser()
      }

      state.inited = true
    })

    const setupUser = async () => {
      setupAxiosDefaults(store, apiAxios);
      await store.dispatch('login');
      const user = store.getters.getUser as UserDTO;
      if (user.dailyBonusState?.isReadyToClaim) {
        popups.value?.getPopups().wheelOfFortunePopup.show(user.dailyBonusState);
      }
      state.userUpdateInterval = setInterval(() => {
        store.dispatch('updateUser')
      }, 5000);

      state.loginUpdateInterval = setInterval(() => {
        store.dispatch('login')
      }, 1000 * 60 * 60);
    }

    onUnmounted(() => {
      clearInterval(nowState.nowUpdateInterval);
      clearInterval(state.userUpdateInterval);
      clearInterval(state.loginUpdateInterval);
    })

    watch(() => store.getters.isLoggedIn, async (newValue, oldValue) => {
      if (newValue) {
        await setupUser()
      }
    })

    return {
      ...toRefs(state),
      loader,
      popups,
      route,
      numberFormatter
    }
  }
})
</script>